import * as Sentry from "@sentry/react";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { AUTH_UNAUTHORIZED_EVENT } from "../services/apiClient";
import { authService } from "../services/authService";

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  account_status?: string;
  trial_end?: string;
  access_expires_at?: string;
  is_trial_expired?: boolean;
  is_access_expired?: boolean;
  has_active_access?: boolean;
  organization_role?: string;
  allowed_features?: string[];
  profile?: {
    job_title: string;
    department: string;
    phone: string;
    preferences: any;
  };
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<any>;
  register: (userData: any) => Promise<boolean>;
  signup: (
    username: string,
    email: string,
    password: string,
  ) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  loading: boolean;
  hasFeature: (feature: string | string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Centralized logout helper — clears React state.
  const clearSession = useCallback(() => {
    setUser(null);
    Sentry.setUser(null);
  }, []);

  // Listen for 401 events dispatched by the shared API client interceptor.
  useEffect(() => {
    const onUnauthorized = () => {
      clearSession();
    };
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    return () => {
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    };
  }, [clearSession]);

  useEffect(() => {
    // Check for existing session on app start (cookie is sent automatically)
    authService
      .getProfile()
      .then((userData) => {
        setUser(userData);
        Sentry.setUser({
          id: String(userData.id),
          email: userData.email,
          username: userData.username,
        });
      })
      .catch(() => {
        // No valid session — user is not logged in
      })
      .finally(() => {
        setLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshUser = async (): Promise<void> => {
    try {
      const userData = await authService.getProfile();
      setUser(userData);
    } catch (error) {
      console.error("Failed to refresh user data:", error);
    }
  };

  const login = async (username: string, password: string): Promise<any> => {
    try {
      const response = await authService.login(username, password);
      setUser(response.user);
      Sentry.setUser({
        id: String(response.user.id),
        email: response.user.email,
        username: response.user.username,
      });
      return response;
    } catch (error) {
      console.error("Login error:", error);
      return false;
    }
  };

  const register = async (userData: any): Promise<boolean> => {
    try {
      const response = await authService.register(userData);
      setUser(response.user);
      Sentry.setUser({
        id: String(response.user.id),
        email: response.user.email,
        username: response.user.username,
      });
      return true;
    } catch (error: any) {
      return false;
    }
  };

  const signup = async (
    username: string,
    email: string,
    password: string,
  ): Promise<boolean> => {
    const userData = {
      username,
      email,
      password,
      password_confirm: password,
    };
    return await register(userData);
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch {
      // Server unreachable — clear local state anyway
    }
    clearSession();
  };

  const hasFeature = useCallback(
    (feature: string | string[]): boolean => {
      if (Array.isArray(feature)) {
        return feature.some((f) => user?.allowed_features?.includes(f) ?? false);
      }
      return user?.allowed_features?.includes(feature) ?? false;
    },
    [user],
  );

  const value = {
    user,
    login,
    register,
    signup,
    logout,
    refreshUser,
    loading,
    hasFeature,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
